import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { formatCurrency } from '@/libs/antd/formater';
import {
	useDownloadSimulation,
	useEditSimulationCalculation,
	useSaveSimulationCalculation,
} from '@/libs/fleet-api/hooks/simulation';
import {
	CalculateSimulationResult,
	SaveSimulationRequest,
} from '@/libs/fleet-api/services/simulation';
import { CheckCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { Button, Col, Descriptions, Divider, message, Row, Table } from 'antd';
import { FC, useState } from 'react';

const SimulationPagePreview: FC<{
	simulationResult: CalculateSimulationResult;
	simulationRequest?: SaveSimulationRequest;
	simulationId?: number;
	viewOnly?: boolean;
}> = ({ simulationResult, simulationRequest, simulationId, viewOnly }) => {
	const { navigate } = useApp();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [simulationNo, setSimulationNo] = useState('');
	const saveMutation = useSaveSimulationCalculation();
	const editMutation = useEditSimulationCalculation();

	console.log({ simulationResult });

	// const customerData = simulationResult;
	const stepsData = simulationResult.stepCreditSimulation;
	const fixedData = simulationResult.fixedCreditSimulation;

	const multiColumns = (values: (string | number | undefined)[]) => (
		// Use a Row as the flex container. Gutter adds space between columns.
		<Row
			gutter={16}
			align="middle"
		>
			{values.map((value, index) => (
				// The key is to use the `flex` prop on Col.
				// `flex={1}` makes each column take up an equal amount of space.
				<Col
					key={index}
					flex={1}
				>
					{value}
				</Col>
			))}
		</Row>
	);

	const stepsStyles = {
		base: {
			contentStyle: {
				textAlign: 'right',
			},
			span: 3,
		},
		header: {
			labelStyle: {
				fontWeight: 'bold',
				backgroundColor: '#E8F1FF',
				fontSize: '16px',
				// This is the key change to remove the divider
				borderInlineEnd: 'none',
			},
			contentStyle: {
				textAlign: 'right',

				backgroundColor: '#E8F1FF',
			},
		},
		highlight: {
			labelStyle: {
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
			contentStyle: {
				textAlign: 'right',

				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
		},
	};

	const fixedStyles = {
		base: {
			span: 3,
			contentStyle: {
				textAlign: 'right',
			},
			labelStyle: {
				fontWeight: 'bold',
			},
		},
		header: {
			labelStyle: {
				fontWeight: 'bold',
				backgroundColor: '#E8F1FF',
				fontSize: '16px',
				// This is the key change to remove the divider
				borderInlineEnd: 'none',
			},
			contentStyle: {
				backgroundColor: '#E8F1FF',
				textAlign: 'right',
			},
		},
		highlight: {
			labelStyle: {
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
				borderInlineEnd: 'none',
			},
			contentStyle: {
				textAlign: 'right',
				fontSize: '16px',
				backgroundColor: '#fffbe6',
				fontWeight: 'bold',
			},
		},
	};

	const stepsTypeItems = [
		// 1. This is your new header item
		{
			key: '0',
			label: 'Detail',
			...stepsStyles.base,
			...stepsStyles.header,
		},
		// 2. Add ...stepsStyles.base to all other items to ensure they take a full row
		{
			key: '1',
			label: 'Nama Lessee',
			children: stepsData?.lesseeName,
			...stepsStyles.base,
		},
		{
			key: '2',
			label: 'Nama PIC Lessee',
			children: stepsData?.lesseePicName,
			...stepsStyles.base,
		},
		{
			key: '3',
			label: 'Jenis Unit',
			children: stepsData?.unit,
			...stepsStyles.base,
		},
		{
			key: '4',
			label: 'Total Unit',
			children: stepsData?.unit,
			...stepsStyles.base,
		},
		{
			key: '5',
			label: 'Tipe Transaksi',
			children: stepsData?.transactionType,
			...stepsStyles.base,
		},
		{
			key: '6',
			label: 'Mata Uang',
			children: stepsData?.currency,
			...stepsStyles.base,
		},
		{
			key: '7',
			label: 'Bunga Eff.',
			children: stepsData?.effectiveRatePercentage + ' %',
			...stepsStyles.base,
		},
		{ key: '8', label: 'Tenor', children: '36 Bulan', ...stepsStyles.base },
		{
			key: '9',
			label: 'Siklus Pembayaran',
			children: stepsData?.paymentCycle,
			...stepsStyles.base,
		},
		{
			key: '10',
			label: 'O/S Principal',
			children: formatCurrency(stepsData?.outstandingPrincipalAmount),
			...stepsStyles.base,
		},
		{
			key: '11',
			label: 'Nilai Pembiayaan',
			children: formatCurrency(stepsData?.financingAmount),
			...stepsStyles.base,
		},
		{
			key: '12',
			label: 'Biaya Admin',
			children: formatCurrency(stepsData?.administrationFee),
			...stepsStyles.base,
		},
		{
			key: '13',
			label: 'Biaya Asuransi',
			children: formatCurrency(stepsData?.insuranceAmount),
			...stepsStyles.base,
		},
		{
			key: '14',
			label: 'Biaya Provisi',
			children: formatCurrency(stepsData?.provisionFee),
			...stepsStyles.base,
		},
		{
			key: '15',
			label: 'Biaya Angsuran Pertama',
			children: formatCurrency(stepsData?.firstInstallmentAmount),
			...stepsStyles.base,
		},
		{
			key: '16',
			label: 'Total Bayar Pertama ke MTF',
			children: formatCurrency(stepsData?.firstPaymentAmount),
			...stepsStyles.base,
			labelStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
			contentStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
		},
		{
			key: '17',
			label: 'Total Bayar DP Murni',
			children: formatCurrency(stepsData?.netDownPaymentAmount),
			...stepsStyles.base,
			labelStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
			contentStyle: { backgroundColor: '#fffbe6', fontWeight: 'bold' },
		},
		{
			key: '18',
			label: 'Komponen Angsuran Per Tahun',
			...stepsStyles.base,
			...stepsStyles.header,
		},
		...(stepsData?.componentInstallments || []).map((installment) => ({
			key: `component-installment-${installment.tenor}`,
			label: `Tahun Ke-${installment.tenor}`,
			span: 24,

			children: (
				<Descriptions
					layout="horizontal"
					column={2}
					size="small"
					contentStyle={{ justifyContent: 'flex-end', display: 'flex' }}
				>
					<Descriptions.Item label="">
						{formatCurrency(installment.installmentPercentage)}%
					</Descriptions.Item>
					<Descriptions.Item label="">
						{formatCurrency(installment.installmentAmount)}
					</Descriptions.Item>
					{/* <Descriptions.Item label="">
						{installment.installmentAmountInMaxUnit}
					</Descriptions.Item> */}
				</Descriptions>
			),
		})),
	];

	const stepsTypeInstallmentMatrixHeader = [
		{
			key: 'installment-matrix-header',
			label: 'Tabel Perhitungan Angsuran',
			...stepsStyles.base,
			...stepsStyles.header,
		},
	];

	const fixedTypeItems = [
		{
			key: '0',
			label: 'Detail',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		{
			key: '1',
			label: 'Merk/Jenis',
			children: fixedData?.assetType,
			...fixedStyles.base,
		},
		{
			key: '2',
			label: 'Dealer',
			children: fixedData?.dealerSupplier,
			...fixedStyles.base,
		},
		{
			key: '3',
			label: 'Tipe Pembiayaan',
			children: fixedData?.paymentType,
			...fixedStyles.base,
		},
		{
			key: '4',
			label: 'Tipe Asuransi',
			children: fixedData?.insuranceType,
			...fixedStyles.base,
		},
		{
			key: '5',
			label: 'Hasil Skema Pembiayaan',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		{
			key: '6',
			label: 'Jenis Pembayaran',
			children: fixedData?.paymentType,
			...fixedStyles.base,
		},
		{
			key: '7',
			label: 'Tenor (Bulan)',
			children: fixedData?.tenorInMonth,
			...fixedStyles.base,
		},
		{
			key: '8',
			label: 'Bunga',
			children: fixedData?.interestPercentage + '%',
			...fixedStyles.base,
		},

		{
			key: '9',
			label: 'Provisi',
			children: fixedData?.provisionPercentage + '%',
			...fixedStyles.base,
		},

		{
			key: '10',
			label: 'Asuransi',
			children: fixedData?.insuranceType,
			...fixedStyles.base,
		},
		{
			key: '11',
			label: 'Hasil Skema Asuransi',
			...fixedStyles.base,
			...fixedStyles.header,
		},
		...(
			fixedData?.insuranceSchemes ||
			simulationResult?.insuranceSchemes ||
			[]
		).map((i, idx) => ({
			key: `fixed-data-insurance-${idx + 1}`,
			label: `Asuransi Tahun ${idx + 1}`,
			children: `${i.coverageType}${
				i.paymentType ? ` (${i.paymentType})` : ''
			}${i.discountAmount ? ` - Disc: ${formatCurrency(i.discountAmount)}` : ''}`,
			...fixedStyles.base,
		})),
		{
			key: '13',
			label: 'Hasil Skema Kredit',
			...fixedStyles.base,
			...fixedStyles.header,
			...fixedStyles.header,
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => i?.unit,
				) || [],
			),
		},
		{
			key: '14',
			label: 'OTR',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.otrAmount),
				) || [],
			),
			...fixedStyles.base,
		},

		// INSURANCE BREAKDOWN
		...Array.from(
			{
				length: Math.max(
					...(
						fixedData?.insuranceSchemes ||
						simulationResult?.insuranceSchemes ||
						[]
					).map((i) => i.tenorInYear),
					0,
				),
			},
			(_, k) => k + 1,
		).map((year) => ({
			key: `insurance-year-${year}`,
			label: `Asuransi Tahun Ke-${year}`,
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(scheme) => {
						const detail = scheme.insuranceAmountDetails?.find(
							(d) => d.tenor === year,
						);
						return formatCurrency(detail ? detail.amount : 0);
					},
				) || [],
			),
			...fixedStyles.base,
		})),
		{
			key: 'total-insurance',
			label: 'Total Asuransi',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(scheme) => {
						return formatCurrency(scheme.fullInsuranceAmount || 0);
					},
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '16',
			label: 'Down Payment',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.downPaymentAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '17',
			label: 'Pokok Hutang',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.principalAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '18',
			label: 'Bunga',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.interestAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '19',
			label: 'Total AR',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.totalInstallmentReceivableAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '20',
			label: 'Angsuran',
			children: multiColumns(
				(fixedData?.creditSchemes || simulationResult?.creditSchemes)?.map(
					(i) => formatCurrency(i?.totalInstallmentAmount),
				) || [],
			),
			...fixedStyles.base,
			...fixedStyles.highlight,
		},
		// PEMBAYARAN PERTAMA
		{
			key: '21',
			label: 'Pembayaran Pertama',
			...fixedStyles.base,
			...fixedStyles.header,
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.unit)) || [],
			),
		},
		{
			key: '22',
			label: 'Down Payment',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.downPaymentAmount),
				) || [],
			),
			...fixedStyles.base,
		},

		{
			key: '23',
			label: 'Asuransi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.insuranceAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '24',
			label: 'Administrasi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.administrationAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '25',
			label: 'Fidusia',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.fiduciaryFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '26',
			label: 'Provisi + Biaya Layanan',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.provisionFee + i.servicingFee),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '27',
			label: 'Provisi',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.provisionFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '28',
			label: 'Biaya Layanan',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) => formatCurrency(i?.servicingFee)) ||
					[],
			),
			...fixedStyles.base,
		},
		{
			key: '29',
			label: 'Angsuran Ke I',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.firstInstallmentAmount),
				) || [],
			),
			...fixedStyles.base,
		},
		{
			key: '30',
			label: 'Total Bayar Pertama',
			children: multiColumns(
				fixedData?.firstPayments?.map((i) =>
					formatCurrency(i?.totalFirstPayment),
				) || [],
			),
			...fixedStyles.base,
			...fixedStyles.highlight,
		},
	];

	const handleSave = () => setShowConfirmModal(true);
	const handleConfirmNo = () => setShowConfirmModal(false);
	const handleConfirmYes = () => {
		if (simulationId) {
			if (!simulationRequest) return;
			editMutation.mutate(
				{ ...simulationRequest, simulationId },
				{
					onSuccess: (result) => {
						setSimulationNo(result.simulationNo);
						setShowConfirmModal(false);
						setShowSuccessModal(true);
					},
					onError: () => {
						setShowConfirmModal(false);
						message.error('Gagal melakukan edit simulasi.');
					},
				},
			);
		} else {
			if (!simulationRequest) return;

			saveMutation.mutate(simulationRequest, {
				onSuccess: (result) => {
					setSimulationNo(result.simulationNo);
					setShowConfirmModal(false);
					setShowSuccessModal(true);
				},
				onError: () => {
					setShowConfirmModal(false);
					message.error('Gagal menyimpan simulasi.');
				},
			});
		}
	};

	const downloadMutation = useDownloadSimulation();
	const [downloadingId, setDownloadingId] = useState<number | null>(null);

	const handleDownload = () => {
		if (!simulationRequest || !simulationId) return;

		setDownloadingId(simulationRequest.spfId);
		downloadMutation.mutate(
			{
				spfId: simulationRequest.spfId,
				facilityId: simulationRequest.facilityId,
				simulationId,
			},
			{
				onSuccess: (data) => {
					setDownloadingId(null);
					if (data?.signedUrl) {
						window.open(data.signedUrl, '_blank');
					} else {
						message.error('Gagal mendapatkan link download.');
					}
				},
				onError: () => {
					setDownloadingId(null);
					message.error('Gagal mendownload simulasi.');
				},
			},
		);
	};

	// Helper to align text based on content
	const getTextAlign = (child: unknown) => {
		if (
			typeof child === 'number' ||
			(typeof child === 'string' &&
				/[\d.,]/.test(child) &&
				!/[a-zA-Z]/.test(child))
		) {
			return 'right';
		}
		return 'left';
	};

	return (
		<div
			style={{
				position: 'relative',
				paddingBottom: 48,
			}}
		>
			<FleetTitle level={2}>Hasil Simulasi Kredit</FleetTitle>
			<Divider style={{ opacity: 0 }} />
			{stepsData !== null ? (
				<>
					<Descriptions
						bordered
						// Set the base column count to 2
						column={3}
						labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
						contentStyle={{ backgroundColor: '#ffffff' }}
					>
						{stepsTypeItems.map((item) => (
							<Descriptions.Item
								key={item.key}
								label={item.label}
								span={'span' in item ? item?.span : undefined} // Assign the span from the item object
								labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
								contentStyle={{
									// @ts-expect-error type
									textAlign: getTextAlign(
										'children' in item ? item.children : undefined,
									),
									...('contentStyle' in item ? item.contentStyle : {}),
								}}
							>
								{'children' in item ? item.children : undefined}
							</Descriptions.Item>
						))}
					</Descriptions>

					{/* INSTALLMENT MATRIX */}
					{/* HEADER */}
					<Descriptions
						bordered
						column={1}
						labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
						contentStyle={{ backgroundColor: '#ffffff' }}
					>
						{stepsTypeInstallmentMatrixHeader.map((item) => (
							<Descriptions.Item
								key={item.key}
								label={item.label}
								span={'span' in item ? item?.span : undefined} // Assign the span from the item object
								labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
								contentStyle={{
									// @ts-expect-error type
									textAlign: getTextAlign(
										'children' in item ? item.children : undefined,
									),
									...('contentStyle' in item ? item.contentStyle : {}),
								}}
							/>
						))}
					</Descriptions>
					{/* BODY */}
					<Table
						dataSource={stepsData.installments}
						columns={[
							{
								title: 'Month',
								align: 'center',
								render: (_, record) => formatCurrency(record.tenorInMonth),
							},
							{
								title: 'PMT',
								align: 'center',
								render: (_, record) => formatCurrency(record.installmentAmount),
							},
							{
								title: 'PMT Rounded',
								align: 'center',
								render: (_, record) =>
									formatCurrency(record.roundedInstallmentAmount),
							},
							{
								title: 'Interest',
								align: 'center',
								render: (_, record) => formatCurrency(record.interestAmount),
							},
							{
								title: 'Principal',
								align: 'center',
								render: (_, record) => formatCurrency(record.principalAmount),
							},
							{
								title: 'O/S Principal',
								align: 'center',
								render: (_, record) =>
									formatCurrency(record.outstandingPrincipalAmount),
							},
						]}
					/>
				</>
			) : fixedData !== null ? (
				<Descriptions
					bordered
					// Set the base column count to 2
					column={3}
					labelStyle={{ width: '50%', backgroundColor: '#ffffff' }}
					contentStyle={{ backgroundColor: '#ffffff' }}
				>
					{fixedTypeItems.map((item) => (
						<Descriptions.Item
							key={item.key}
							label={item.label}
							span={'span' in item ? item?.span : undefined} // Assign the span from the item object
							labelStyle={'labelStyle' in item ? item.labelStyle : undefined}
							contentStyle={{
								// @ts-expect-error type
								textAlign: getTextAlign(
									'children' in item ? item.children : undefined,
								),
								...('contentStyle' in item ? item.contentStyle : {}),
							}}
						>
							{'children' in item ? item.children : undefined}
						</Descriptions.Item>
					))}
				</Descriptions>
			) : null}
			<Divider style={{ opacity: 0, marginTop: 4 }} />

			{!viewOnly && (
				<div
					style={{
						position: 'fixed',
						bottom: 0,
						left: 0,
						width: '100vw',
						background: '#fff',
						borderTop: '1px solid #e6e6e6',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 16,
						padding: '16px 32px',
						zIndex: 10,
					}}
				>
					{simulationId && (
						<Button
							type="default"
							style={{
								minWidth: 180,
								fontWeight: 600,
								color: '#1557bc',
								border: '1px solid #1557bc',
								background: '#fff',
							}}
							loading={
								downloadingId === simulationRequest?.spfId &&
								downloadMutation.status === 'pending'
							}
							onClick={handleDownload}
						>
							DOWNLOAD SIMULASI
						</Button>
					)}
					<Button
						type="primary"
						onClick={handleSave}
						style={{
							minWidth: 120,
							fontWeight: 600,
							background: '#1557bc',
							color: '#fff',
							border: 'none',
						}}
						loading={saveMutation.status === 'pending'}
						disabled={saveMutation.status === 'pending'}
					>
						SAVE
					</Button>
				</div>
			)}

			{/* Confirmation Modal */}
			<FleetModal
				open={showConfirmModal}
				onCancel={handleConfirmNo}
				closable={false}
				maskClosable={false}
				footer={null}
				width={420}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 16, marginBottom: 32 }}>
					Apakah hasil simulasi kredit yang ditampilkan sesuai dengan
					perhitungan Anda?
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={handleConfirmNo}
						type="default"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							border: '1px solid #1557BC',
							color: '#1557BC',
							background: 'white',
						}}
					>
						TIDAK
					</Button>
					<Button
						onClick={handleConfirmYes}
						type="primary"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							background: '#1557BC',
							color: 'white',
							border: 'none',
						}}
					>
						YA
					</Button>
				</div>
				<div
					style={{
						position: 'absolute',
						left: 0,
						bottom: 0,
						width: '100%',
						height: 8,
						display: 'flex',
					}}
				>
					<div
						style={{ background: '#1557BC', height: '100%', width: '85%' }}
					/>
					<div
						style={{ background: '#FFB300', height: '100%', width: '15%' }}
					/>
				</div>
			</FleetModal>

			{/* Success Modal */}
			<FleetModal
				open={showSuccessModal}
				onCancel={() => setShowSuccessModal(false)}
				onOk={() => navigate('FRMSimulationListPage')}
				closable={false}
				maskClosable={false}
				width={420}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<CheckCircleFilled
						style={{ fontSize: 48, color: '#3CB371', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
					Nomor Simulasi Kredit Berhasil Dibuat!
				</div>
				<div style={{ fontWeight: 700, fontSize: 22, marginBottom: 32 }}>
					{simulationNo}
				</div>
				<div
					style={{
						position: 'absolute',
						left: 0,
						bottom: 0,
						width: '100%',
						height: 8,
						display: 'flex',
					}}
				>
					<div
						style={{ background: '#1557BC', height: '100%', width: '85%' }}
					/>
					<div
						style={{ background: '#FFB300', height: '100%', width: '15%' }}
					/>
				</div>
			</FleetModal>
		</div>
	);
};

export default SimulationPagePreview;
